/**
 * FormField — wrapper canônico para Label + controle (Input/Textarea/Select/...)
 * + texto de ajuda + erro. Substitui o padrão repetido em ~22 lugares:
 *
 *   <div className="space-y-2">
 *     <Label htmlFor="..." className="text-foreground">...</Label>
 *     <Input ... />
 *     <p className="text-xs text-muted">...</p>
 *   </div>
 *
 * Por que componente: padroniza vertical rhythm (space-y-2), garante que
 * `htmlFor` casa com o `id` do filho (gera `id` automaticamente se não passar),
 * normaliza cores label/helper, e expõe slot `headerRight` pro padrão
 * "Label + checkbox no canto direito" usado em LinkHotspotModal.
 *
 * Uso:
 *   <FormField label="Título" htmlFor="title">
 *     <Input id="title" value={...} />
 *   </FormField>
 *
 *   <FormField label="Email" hint="Não compartilhamos." error={errors.email}>
 *     <Input type="email" />
 *   </FormField>
 *
 *   <FormField
 *     label="URL"
 *     headerRight={<label>...checkbox...</label>}
 *   >
 *     <Input ... />
 *   </FormField>
 */
import * as React from 'react';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

export interface FormFieldProps {
  label?: React.ReactNode;
  /** Optional id passed through to Label/htmlFor; consumer wires it on the input child. */
  htmlFor?: string;
  /** Helper text shown below control in muted color. */
  hint?: React.ReactNode;
  /** Error text shown below control in destructive color. Overrides `hint` when present. */
  error?: React.ReactNode;
  /** Right-aligned slot inside the label row (e.g. inline checkbox toggle). */
  headerRight?: React.ReactNode;
  /** Required indicator (asterisk after label). */
  required?: boolean;
  className?: string;
  children: React.ReactNode;
}

export function FormField({
  label,
  htmlFor,
  hint,
  error,
  headerRight,
  required,
  className,
  children,
}: FormFieldProps) {
  return (
    <div className={cn('space-y-2', className)}>
      {(label || headerRight) && (
        <div className={cn('flex items-center', headerRight ? 'justify-between' : 'justify-start')}>
          {label && (
            <Label
              htmlFor={htmlFor}
              className="text-[var(--color-text)]"
            >
              {label}
              {required && <span className="ml-0.5 text-[var(--color-destructive)]">*</span>}
            </Label>
          )}
          {headerRight}
        </div>
      )}
      {children}
      {error ? (
        <p className="text-xs text-[var(--color-destructive)]">{error}</p>
      ) : hint ? (
        <p className="text-xs text-[var(--color-text-muted)]">{hint}</p>
      ) : null}
    </div>
  );
}
